import { useEffect, useMemo, useState } from 'react'
import { apiGet } from '../lib/api.js'
import { buildWhtModel, money2 } from '../lib/wht.js'

// Standalone printable หนังสือรับรองการหักภาษี ณ ที่จ่าย (50 ทวิ). Opened in a new
// tab as /expense/wht?id=<expenseId>. Print / Save-as-PDF → A4, one page.
// Payer block = the branch's COMPANY object; payee = the expense's seller.

const Box = ({ on }) => (
  <span style={{
    display: 'inline-block', width: 11, height: 11, border: '1px solid #000',
    marginRight: 5, position: 'relative', top: 1, textAlign: 'center', lineHeight: '10px', fontSize: 10,
  }}>{on ? '✓' : ''}</span>
)

export default function WhtCertificate() {
  const id = useMemo(() => new URLSearchParams(window.location.search).get('id'), [])
  const [expense, setExpense] = useState(null)
  const [branch, setBranch] = useState(null)
  const [err, setErr] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!id) { setErr('ไม่พบรหัสค่าใช้จ่าย'); setLoading(false); return }
    let alive = true
    apiGet('/api/expense/' + id)
      .then(async (e) => {
        if (!alive) return
        setExpense(e)
        const bid = e?.branch_id
        if (bid) { try { setBranch(await apiGet('/api/branches/' + bid)) } catch { /* company optional */ } }
      })
      .catch((e) => alive && setErr('โหลดข้อมูลไม่สำเร็จ: ' + (e.message || e)))
      .finally(() => alive && setLoading(false))
    return () => { alive = false }
  }, [id])

  const m = useMemo(() => (expense ? buildWhtModel(expense, branch) : null), [expense, branch])

  useEffect(() => { document.title = m ? `WHT ${m.docNo}` : 'WHT' }, [m])

  if (loading) return <Center>กำลังโหลด…</Center>
  if (err) return <Center>{err}</Center>
  if (!m) return <Center>ไม่มีข้อมูล</Center>
  if (!expense?.amount?.WHT_add) return <Center>ค่าใช้จ่ายนี้ไม่มีการหักภาษี ณ ที่จ่าย</Center>

  return (
    <div className="wht-root">
      <style>{CSS}</style>
      <div className="wht-toolbar" id="no-print">
        <button onClick={() => window.print()}>🖨️ พิมพ์ / บันทึก PDF</button>
        <span className="hint">A4 · 1 หน้า — ตั้งค่าเครื่องพิมพ์เป็น A4 และปิด “Headers and footers”</span>
      </div>

      <div className="sheet">
        {/* copy labels */}
        <div className="copies">
          <div><Box /> ฉบับที่ 1 (สำหรับผู้ถูกหักภาษี ณ ที่จ่าย ใช้แนบพร้อมกับแบบแสดงรายการภาษี)</div>
          <div><Box /> ฉบับที่ 2 (สำหรับผู้ถูกหักภาษี ณ ที่จ่าย เก็บไว้เป็นหลักฐาน)</div>
          <div><Box /> ฉบับที่ 3 (สำหรับผู้หักภาษี ณ ที่จ่าย เก็บไว้เป็นหลักฐาน)</div>
        </div>

        {/* title */}
        <div className="title-row">
          <div className="title">
            <div className="t1">หนังสือรับรองการหักภาษี ณ ที่จ่าย</div>
            <div className="t2">ตามมาตรา 50 ทวิ แห่งประมวลรัษฎากร</div>
          </div>
          <div className="docno">เลขที่ {m.docNo}</div>
        </div>

        {/* parties */}
        <div className="parties">
          <Party title="ผู้มีหน้าที่หักภาษี ณ ที่จ่าย" p={m.payer} />
          <Party title="ผู้ถูกหักภาษี ณ ที่จ่าย" p={m.payee} />
        </div>

        {/* form-type checkboxes */}
        <div className="forms">
          <span className="lam">ลำดับที่ ........ ในแบบ</span>
          <div className="fgrid">
            <span className="fbox"><Box /> (1) ภ.ง.ด. 1ก</span>
            <span className="fbox"><Box /> (2) ภ.ง.ด. 1ก พิเศษ</span>
            <span className="fbox"><Box on={m.form === 'pnd2'} /> (3) ภ.ง.ด. 2</span>
            <span className="fbox"><Box on={m.form === 'pnd3'} /> (4) ภ.ง.ด. 3</span>
            <span className="fbox"><Box /> (5) ภ.ง.ด. 2ก</span>
            <span className="fbox"><Box /> (6) ภ.ง.ด. 3ก</span>
            <span className="fbox"><Box on={m.form === 'pnd53'} /> (7) ภ.ง.ด. 53</span>
          </div>
        </div>

        {/* income table */}
        <table className="tbl">
          <thead>
            <tr>
              <th>ประเภทเงินได้พึงประเมินที่จ่าย</th>
              <th className="c2">วัน เดือน หรือ ปีภาษีที่จ่าย</th>
              <th className="c3">จำนวนเงินที่จ่าย</th>
              <th className="c4">ภาษีที่หักและนำส่งไว้</th>
            </tr>
          </thead>
          <tbody>
            {/* items 1–6 live in ONE cell — no horizontal separators (matches the form) */}
            <tr>
              <td className="lc">
                <div className="ln">1. เงินเดือน ค่าจ้าง เบี้ยเลี้ยง โบนัส ฯลฯ ตามมาตรา 40 (1)</div>
                <div className="ln">2. ค่าธรรมเนียม ค่านายหน้า ฯลฯ ตามมาตรา 40 (2)</div>
                <div className="ln">3. ค่าแห่งลิขสิทธิ์ ฯลฯ ตามมาตรา 40 (3)</div>
                <div className="ln">4. (ก) ดอกเบี้ย ฯลฯ ตามมาตรา 40 (4) (ก)</div>
                <div className="ln">&nbsp;&nbsp;&nbsp;(ข) เงินปันผล เงินส่วนแบ่งกำไร ฯลฯ ตามมาตรา 40 (4) (ข)</div>
                <div className="sub">
                  <div>(1) กรณีผู้ได้รับเงินปันผลได้รับเครดิตภาษี โดยจ่ายจากกำไรสุทธิของกิจการที่ต้องเสียภาษีเงินได้นิติบุคคลในอัตราดังนี้</div>
                  <div className="i2">(1.1) อัตราร้อยละ 30 ของกำไรสุทธิ&nbsp;&nbsp;(1.2) อัตราร้อยละ 25 ของกำไรสุทธิ</div>
                  <div className="i2">(1.3) อัตราร้อยละ 20 ของกำไรสุทธิ&nbsp;&nbsp;(1.4) อัตราอื่นๆ (ระบุ) ......... ของกำไรสุทธิ</div>
                  <div>(2) กรณีผู้ได้รับเงินปันผลไม่ได้รับเครดิตภาษี เนื่องจากจ่ายจาก</div>
                  <div className="i2">(2.1) กำไรสุทธิของกิจการที่ได้รับยกเว้นภาษีเงินได้นิติบุคคล</div>
                  <div className="i2">(2.2) เงินปันผลหรือเงินส่วนแบ่งของกำไรที่ได้รับยกเว้นไม่ต้องนำมารวมคำนวณเป็นรายได้เพื่อเสียภาษีเงินได้นิติบุคคล</div>
                  <div className="i2">(2.3) กำไรสุทธิส่วนที่ได้หักผลขาดทุนสุทธิยกมาไม่เกิน 5 ปี ก่อนรอบระยะเวลาบัญชีปัจจุบัน</div>
                  <div className="i2">(2.4) กำไรที่รับรู้ทางบัญชีโดยวิธีส่วนได้เสีย (equity method)</div>
                  <div className="i2">(2.5) อื่นๆ (ระบุ) ..................................................</div>
                </div>
                <div className="ln">5. การจ่ายเงินได้ที่ต้องหักภาษี ณ ที่จ่าย ตามคำสั่งกรมสรรพากรที่ออกตามมาตรา 3 เตรส เช่น รางวัล ส่วนลดหรือประโยชน์ใดๆ เนื่องจากการส่งเสริมการขาย รางวัลในการประกวด การแข่งขัน การชิงโชค ค่าแสดงของนักแสดงสาธารณะ ค่าจ้างทำของ ค่าโฆษณา ค่าเช่า ค่าบริการ ค่าเบี้ยประกันวินาศภัย ฯลฯ</div>
                <div className="ln">6. อื่นๆ ระบุ {m.incomeType || '................'}</div>
              </td>
              <td className="c2 val vb">{m.payDateBE}</td>
              <td className="c3 val vb">{money2(m.base)}</td>
              <td className="c4 val vb">{money2(m.wht)}</td>
            </tr>
            <tr className="sum">
              <td className="lc">รวมเงินที่จ่ายและภาษีที่หักนำส่ง</td>
              <td className="c2" />
              <td className="c3 val">{money2(m.base)}</td>
              <td className="c4 val">{money2(m.wht)}</td>
            </tr>
          </tbody>
        </table>

        {/* total in words */}
        <div className="words">
          <span>รวมเงินที่จ่ายและภาษีที่หักนำส่ง (ตัวอักษร)</span>
          <b>{m.whtText}</b>
        </div>

        <div className="funds">
          เงินที่จ่ายเข้า กบข./กสจ./กองทุนสงเคราะห์ครูโรงเรียนเอกชน ......... บาท&nbsp;
          กองทุนประกันสังคม ......... บาท&nbsp; กองทุนสำรองเลี้ยงชีพ ......... บาท
        </div>

        <div className="payer-choice">
          <span className="pl">ผู้จ่ายเงิน</span>
          <span className="fbox"><Box on /> (1) หัก ณ ที่จ่าย</span>
          <span className="fbox"><Box /> (2) ออกให้ตลอดไป</span>
          <span className="fbox"><Box /> (3) ออกให้ครั้งเดียว</span>
          <span className="fbox"><Box /> (4) อื่นๆ (ระบุ) ....................</span>
        </div>

        {/* footer / signature */}
        <div className="foot">
          <div className="warn">
            <b>คำเตือน :</b> ผู้มีหน้าที่ออกหนังสือรับรองการหักภาษี ณ ที่จ่าย
            ฝ่าฝืนไม่ปฏิบัติตามมาตรา 50 ทวิ แห่งประมวลรัษฎากร
            ต้องรับโทษทางอาญาตามมาตรา 35 แห่งประมวลรัษฎากร
          </div>
          <div className="sign">
            <div className="certify">ขอรับรองว่าข้อความและตัวเลขดังกล่าวถูกต้องตรงกับความจริงทุกประการ</div>
            <img className="sigimg" src="/wht-signature.png" alt="" onError={(e) => { e.currentTarget.style.display = 'none' }} />
            <div className="signline">{m.signatoryName} (กรรมการ{m.payer.name || 'บริษัท'})</div>
            <div className="issue">วันที่ออกใบรับรอง {m.issueDateBE}</div>
          </div>
          <div className="stamp"><span>ประทับตรา<br />นิติบุคคล<br />(ถ้ามี)</span></div>
        </div>
      </div>
    </div>
  )
}

function Party({ title, p }) {
  return (
    <div className="party">
      <div className="ptitle">{title}</div>
      <div className="prow"><span className="pk">ชื่อ</span><span className="pv">{p.name || '—'}</span></div>
      <div className="prow"><span className="pk">ที่อยู่</span><span className="pv">{p.address || '—'}</span></div>
      <div className="prow"><span className="pk">เลขประจำตัวผู้เสียภาษี</span><span className="pv">{p.taxid || '—'}</span></div>
    </div>
  )
}

function Center({ children }) {
  return <div style={{ fontFamily: "'Noto Sans Thai',sans-serif", padding: 40, color: '#475569' }}>{children}</div>
}

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Sarabun:wght@400;500;600;700&display=swap');
* { box-sizing: border-box; }
.wht-root { font-family: 'Sarabun', 'Noto Sans Thai', sans-serif; color: #000; background: #f1f5f9; }
.wht-toolbar { display: flex; align-items: center; gap: 14px; padding: 12px 16px; background: #fff; border-bottom: 1px solid #e2e8f0; position: sticky; top: 0; z-index: 100; box-shadow: 0 1px 4px rgba(0,0,0,.06); }
.wht-toolbar button { background: #4f46e5; color: #fff; border: 0; border-radius: 8px; padding: 8px 16px; font-size: 14px; font-weight: 600; cursor: pointer; font-family: inherit; }
.wht-toolbar .hint { font-size: 12px; color: #64748b; }

.sheet { position: relative; z-index: 0; width: 210mm; min-height: 297mm; margin: 16px auto; background: #fff; padding: 8mm 11mm; box-shadow: 0 2px 12px rgba(0,0,0,.12); font-size: 12px; line-height: 1.4; }

.copies { font-size: 10.5px; }
.copies > div { margin: 2px 0; }

.title-row { position: relative; margin: 8px 0 10px; }
.title { text-align: center; }
.title .t1 { font-size: 18px; font-weight: 700; }
.title .t2 { font-size: 12px; }
.docno { position: absolute; right: 0; bottom: 0; font-size: 12px; }

.parties { display: flex; gap: 0; border: 1px solid #000; }
.party { flex: 1; padding: 8px 10px; }
.party + .party { border-left: 1px solid #000; }
.ptitle { font-weight: 700; font-size: 12.5px; margin-bottom: 4px; color: #2563eb; }
.prow { display: flex; gap: 5px; align-items: flex-start; margin: 3px 0; }
.prow .pk { color: #000; white-space: nowrap; }
.prow .pv { flex: 1; }

.forms { display: flex; align-items: flex-start; gap: 14px; margin: 9px 2px; font-size: 11.5px; }
.forms .lam { white-space: nowrap; padding-top: 1px; }
.fgrid { flex: 1; display: grid; grid-template-columns: repeat(4, 1fr); gap: 6px 16px; }
.fbox { white-space: nowrap; }

.tbl { width: 100%; border-collapse: collapse; margin-top: 2px; }
.tbl th, .tbl td { border: 1px solid #000; padding: 6px 7px; vertical-align: top; }
.tbl th { font-size: 11px; font-weight: 700; text-align: center; background: #fff; white-space: nowrap; padding: 7px 7px; }
.tbl th.c2, .tbl td.c2 { text-align: center; }
.tbl th.c3, .tbl td.c3 { width: 92px; text-align: right; }
.tbl th.c4, .tbl td.c4 { text-align: right; }
.tbl td.lc { font-size: 11.5px; line-height: 1.6; }
.tbl td.val { font-weight: 600; }
.tbl td.vb { vertical-align: bottom; }
.ln { margin: 4px 0; }
.sub { font-size: 10px; padding-left: 14px; }
.sub > div { margin: 1px 0; }
.sub .i2 { padding-left: 16px; }
.tbl tr.sum td { font-weight: 700; text-align: center; }
.tbl tr.sum td.lc { text-align: right; }
.tbl tr.sum td.c3, .tbl tr.sum td.c4 { text-align: right; }

.words { display: flex; justify-content: space-between; gap: 10px; border: 1px solid #000; border-top: 0; padding: 6px 10px; font-size: 12px; }
.words b { font-weight: 700; }
.funds { border: 1px solid #000; border-top: 0; padding: 6px 10px; font-size: 11px; }
.payer-choice { display: flex; flex-wrap: wrap; align-items: center; gap: 3px 14px; border: 1px solid #000; border-top: 0; padding: 6px 10px; font-size: 11.5px; }
.payer-choice .pl { font-weight: 700; margin-right: 6px; }

.foot { display: flex; border: 1px solid #000; border-top: 0; min-height: 104px; }
.warn { width: 35%; padding: 8px 10px; font-size: 10px; line-height: 1.5; border-right: 1px solid #000; }
.sign { flex: 1; padding: 9px 10px; text-align: center; position: relative; }
.certify { font-size: 11.5px; margin-bottom: 6px; }
.sigimg { max-height: 52px; max-width: 190px; object-fit: contain; margin: 2px auto 0; display: block; }
.signline { margin-top: 6px; font-size: 12px; }
.issue { font-size: 11px; margin-top: 3px; }
.stamp { width: 108px; border-left: 1px solid #000; display: flex; align-items: center; justify-content: center; }
.stamp span { width: 78px; height: 78px; border: 1px dashed #94a3b8; border-radius: 50%; display: flex; align-items: center; justify-content: center; text-align: center; font-size: 9px; color: #64748b; }

@page { size: A4; margin: 0; }
@media print {
  .wht-root { background: #fff; }
  #no-print { display: none !important; }
  .sheet { width: 210mm; min-height: 297mm; margin: 0; box-shadow: none; }
}
`
